// MerchantOrdersScreen/ratingFeed.js
//
// ХЭРЭГЛЭГЧИЙН ҮНЭЛГЭЭ — «Мэдээ, мэдээлэл» картанд байнга (2026-09-18, эзний шийдвэр).
// Сүүлийнх нь эхэндээ; үнэлгээ бүрт захиалсан хоолны жижиг зураг. 09-17-ны хажуугаас
// гулсаж гардаг богино мэдэгдлийг орлоно.
//
// • Анх нээхэд сүүлийн 10, «Цааш үзэх» — өмнөх 10 (before=id).
// • 3 минут тутам (таб нээлттэй БА хэсэг харагдаж байх үед) шинийг шалгана (after=id), «Шинэ» тэмдэгтэй.
// • Захиалагчийн нэр, утас сервер өгдөггүй.

import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { getRatingsFeed } from "../../api/axios/requests";
import "./ratingFeed.css";

/* Ачаалал багатай (эзний заавар 2026-09-18): 3 минут тутам, ЗӨВХӨН таб нээлттэй БА
   энэ хэсэг дэлгэц дээр харагдаж байх үед. Буцаж ирэхэд хамгийн багадаа MIN_GAP_MS
   өнгөрсөн бол л нэг удаа шалгана. */
const POLL_MS = 3 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;
const FRESH_MS = 60 * 1000;

function timeLabel(value) {
  const t = dayjs(value);
  if (!t.isValid()) return "";
  const min = dayjs().diff(t, "minute");
  if (min < 1) return "Дөнгөж сая";
  if (min < 60) return `${min} мин өмнө`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} цагийн өмнө`;
  return t.format("MM/DD HH:mm");
}

function Stars({ value }) {
  const r = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span className="rating-feed__stars" aria-label={`${r} од`} title={`${r} од`}>
      {"★".repeat(r)}
      <span className="rating-feed__stars-off">{"★".repeat(5 - r)}</span>
    </span>
  );
}

// Хоол бүр тусдаа зурагтай, дараалж (давхцахгүй); олон ширхэгтэй бол «×2» тэмдэг.
function Thumb({ product }) {
  const [broken, setBroken] = useState(false);
  const title = product.qty > 1 ? `${product.name} ×${product.qty}` : product.name;
  return (
    <span className="rating-feed__thumb" title={title}>
      {product.image && !broken ? (
        <img src={product.image} alt={product.name} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <i className="mdi mdi-food-outline" aria-hidden="true" />
      )}
      {product.qty > 1 && <span className="rating-feed__qty">×{product.qty}</span>}
    </span>
  );
}

// Нэг захиалга = нэг мөр: од, цаг → хоолнуудын зураг дараалан → нэрс → сэтгэгдэл.
function RatingRow({ item, fresh }) {
  const products = Array.isArray(item.products) ? item.products : [];
  const names = products.map((p) => (p.qty > 1 ? `${p.name} ×${p.qty}` : p.name)).join(" · ");
  return (
    <li className={`rating-feed__row${fresh ? " rating-feed__row--fresh" : ""}`}>
      <div className="rating-feed__head">
        <Stars value={item.rating} />
        {fresh && <span className="badge bg-success-subtle text-success">Шинэ</span>}
        <span className="rating-feed__time">{timeLabel(item.createdAt)}</span>
      </div>
      {products.length > 0 && (
        <div className="rating-feed__thumbs">
          {products.map((p, i) => (
            <Thumb key={`${p.id}-${i}`} product={p} />
          ))}
          {item.moreCount > 0 && <span className="rating-feed__more">+{item.moreCount}</span>}
        </div>
      )}
      <div className="rating-feed__names">{names || `Захиалга #${item.orderId}`}</div>
      {item.comment && <div className="rating-feed__comment">“{item.comment}”</div>}
    </li>
  );
}

export default function RatingFeed() {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [fresh, setFresh] = useState(() => new Set());
  const topIdRef = useRef(null);
  const lastPollRef = useRef(0);
  const onScreenRef = useRef(false);
  const rootRef = useRef(null);
  // Хугацаа шинэчлэх («5 мин өмнө») — минут тутам.
  const [, setTick] = useState(0);

  const merge = useCallback((incoming, { atTop }) => {
    setItems((list) => {
      const seen = new Set(list.map((i) => i.id));
      const add = incoming.filter((i) => !seen.has(i.id));
      const next = atTop ? [...add, ...list] : [...list, ...add];
      next.sort((a, b) => b.id - a.id);
      if (next.length) topIdRef.current = next[0].id;
      return next;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    let busy = false;
    const timers = [];

    const firstLoad = async () => {
      try {
        const res = await getRatingsFeed({});
        if (!alive) return;
        if (!res?.success) throw new Error("feed");
        merge(res.data?.items || [], { atTop: false });
        setHasMore(Boolean(res.data?.hasMore));
        setError(false);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    };

    const poll = async () => {
      // Таб далд, эсвэл хэсэг дэлгэцэн дээр харагдахгүй бол сервер рүү огт хандахгүй.
      if (busy || document.hidden || !onScreenRef.current) return;
      if (Date.now() - lastPollRef.current < MIN_GAP_MS) return;
      lastPollRef.current = Date.now();
      if (topIdRef.current == null) {
        // Анхны ачаалал амжилтгүй эсвэл үнэлгээ огт байгаагүй — дахин эхнээс нь.
        await firstLoad();
        return;
      }
      busy = true;
      try {
        const res = await getRatingsFeed({ after: topIdRef.current });
        const add = res?.success ? res.data?.items || [] : [];
        if (!alive || !add.length) return;
        merge(add, { atTop: true });
        const ids = add.map((i) => i.id);
        setFresh((s) => new Set([...s, ...ids]));
        timers.push(
          setTimeout(() => {
            if (alive) setFresh((s) => new Set([...s].filter((id) => !ids.includes(id))));
          }, FRESH_MS),
        );
      } catch {
        /* сүлжээ — дараагийн удаа */
      } finally {
        busy = false;
      }
    };

    firstLoad();
    lastPollRef.current = Date.now();
    const iv = setInterval(poll, POLL_MS);
    // Цагийн бичвэр («5 мин өмнө») — сервер рүү хандалтгүй, зөвхөн дахин зурна.
    const clock = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    const onVisible = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener("visibilitychange", onVisible);

    /* Хэсэг дэлгэцэн дээр гарч ирэхэд л шалгана — хуудасны доод хэсэгт байгаа
       (харагдахгүй) үед сервер рүү хүсэлт явуулахгүй. */
    let io = null;
    if (typeof IntersectionObserver !== "undefined" && rootRef.current) {
      io = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((e) => e.isIntersecting);
          const appeared = visible && !onScreenRef.current;
          onScreenRef.current = visible;
          if (appeared) poll();
        },
        { rootMargin: "120px" },
      );
      io.observe(rootRef.current);
    } else {
      onScreenRef.current = true; // хуучин хөтөч — хуучнаараа
    }

    return () => {
      alive = false;
      clearInterval(iv);
      clearInterval(clock);
      timers.forEach(clearTimeout);
      document.removeEventListener("visibilitychange", onVisible);
      if (io) io.disconnect();
    };
  }, [merge]);

  const loadMore = async () => {
    if (loadingMore || !items.length) return;
    setLoadingMore(true);
    try {
      const res = await getRatingsFeed({ before: items[items.length - 1].id });
      if (res?.success) {
        merge(res.data?.items || [], { atTop: false });
        setHasMore(Boolean(res.data?.hasMore));
      }
    } catch {
      /* товч хэвээр үлдэнэ — дахин дарж болно */
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="rating-feed" ref={rootRef}>
      <div className="rating-feed__title">
        <h5 className="mb-0">Хэрэглэгчийн үнэлгээ</h5>
        <span className="text-muted small">Сүүлийнх нь эхэндээ</span>
      </div>

      {loading ? (
        <ul className="rating-feed__list placeholder-glow" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="rating-feed__row">
              <span className="placeholder rating-feed__thumb" />
              <span className="placeholder col-7" />
            </li>
          ))}
        </ul>
      ) : error && !items.length ? (
        <p className="text-muted small mb-0">Үнэлгээ ачаалж чадсангүй. Хэсэг хугацааны дараа дахин шалгана.</p>
      ) : !items.length ? (
        <p className="text-muted small mb-0">Одоогоор үнэлгээ алга. Хэрэглэгч үнэлмэгц энд гарна.</p>
      ) : (
        <>
          <ul className="rating-feed__list" aria-live="polite">
            {items.map((item) => (
              <RatingRow key={item.id} item={item} fresh={fresh.has(item.id)} />
            ))}
          </ul>
          {hasMore && (
            <button
              type="button"
              className="btn btn-sm btn-light w-100 mt-2"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Ачаалж байна…" : "Цааш үзэх"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
